/**
 * SmsCampaignForm (MKT-REDESIGN-01 M6, 2026-09-03) — a TEXT campaign.
 * Consent-first, as locked in the Marketing proposal: marketing texts go ONLY
 * to customers with an opt-in on record (the office recorded a yes, the
 * customer texted OFFERS, the portal, a form). Everyone else keeps getting
 * email and the transactional texts they already get. The message is written
 * here (short, with {{first_name}} / {{portal_url}}), the opt-out line is
 * added automatically, and sends run 9 AM – 8 PM Central only.
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, MessageSquare, Send, Users } from "lucide-react";
import { SlideOver } from "@/components/ui/SlideOver";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { supabase } from "@/lib/supabase";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useCommsLive } from "@/features/comms/comms";
import type { AudiencePick } from "./AudiencesPanel";
import { resolveAudience, useAudiences, STARTER_AUDIENCES, type AudienceCount } from "./audiences";
import { estimateDuration, useLaunchCampaign, type CampaignRecipient } from "./campaigns";

// ---- SMS length: GSM-7 (160 / 153 per segment) vs Unicode (70 / 67) ----
const GSM = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = "^{}\\[~]|€";
export function smsSegments(text: string): { chars: number; segments: number; unicode: boolean; culprit: string | null } {
  let unicode = false; let len = 0; let culprit: string | null = null;
  for (const ch of text) {
    if (GSM.includes(ch)) len += 1;
    else if (GSM_EXT.includes(ch)) len += 2;
    else { unicode = true; culprit = culprit ?? ch; }
  }
  if (unicode) { const n = [...text].length; return { chars: n, segments: n <= 70 ? 1 : Math.ceil(n / 67), unicode, culprit }; }
  return { chars: len, segments: len <= 160 ? 1 : Math.ceil(len / 153), unicode, culprit };
}
const STOP_LINE = "\nReply STOP to opt out";
const PORTAL_SAMPLE = `https://my.sedgwickheating.com/portal?t=${"0".repeat(36)}`;
/** What one customer would actually receive (the database does the same at schedule time). */
export function renderSmsSample(body: string, firstName = "Sarah"): string {
  const withTokens = body.replace(/\{\{\s*first_name\s*\}\}/gi, firstName).replace(/\{\{\s*portal_url\s*\}\}/gi, PORTAL_SAMPLE);
  return withTokens.replace(/\s+$/, "") + (/\bSTOP\b/i.test(body) ? "" : STOP_LINE);
}

export function SmsCampaignForm({ open, onClose, initialPick }: { open: boolean; onClose: () => void; initialPick?: AudiencePick | null }) {
  const live = useCommsLive().data ?? false;
  const canSend = useCan("marketing.send");
  const launch = useLaunchCampaign();
  const { data: savedAudiences } = useAudiences();
  const options = useMemo(() => [
    ...(savedAudiences ?? []).map((a) => ({ key: `saved:${a.id}`, label: a.name, pick: { label: a.name, audienceId: a.id, spec: a.mode === "static" && a.static_custnos?.length ? { custnos: a.static_custnos, active_only: false } : a.spec } as AudiencePick })),
    ...STARTER_AUDIENCES.map((s) => ({ key: `starter:${s.key}`, label: `${s.name} (starter)`, pick: { label: s.name, spec: s.spec } as AudiencePick })),
  ], [savedAudiences]);
  const [pickKey, setPickKey] = useState("");
  const pick = options.find((o) => o.key === pickKey)?.pick ?? null;
  const [count, setCount] = useState<AudienceCount | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipient[] | null>(null);
  const [counting, setCounting] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState<"now" | "later">("now");
  const [at, setAt] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [showHelp, setShowHelp] = useState(false);

  // A pick handed in from the Audiences panel ("Text this audience").
  useEffect(() => {
    if (!open) return;
    if (initialPick) {
      const match = options.find((o) => (initialPick.audienceId ? o.key === `saved:${initialPick.audienceId}` : o.pick.label === initialPick.label));
      if (match) setPickKey(match.key);
      if (!name) setName(`Text: ${initialPick.label}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPick, options.length]);

  // Count + resolve whenever the audience changes (channel = sms).
  useEffect(() => {
    let cancelled = false;
    if (!pick) { setCount(null); setRecipients(null); return; }
    setCounting(true); setCount(null); setRecipients(null);
    (async () => {
      try {
        const [{ data }, resolved] = await Promise.all([
          supabase.rpc("pegasus_audience_count", { p_spec: pick.spec, p_channel: "sms" }),
          resolveAudience(pick.spec, "sms"),
        ]);
        if (cancelled) return;
        setCount((data as AudienceCount) ?? null);
        setRecipients(resolved.recipients);
      } finally { if (!cancelled) setCounting(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickKey]);

  const sample = renderSmsSample(message);
  const seg = smsSegments(sample);
  const n = recipients?.length ?? 0;
  const tooLong = message.length > 600;
  const ready = !!pick && n > 0 && message.trim().length > 0 && !tooLong && name.trim().length > 0 && canSend && !launch.isPending;
  const insertToken = (t: string) => setMessage((m) => (m.endsWith(" ") || m.length === 0 ? m : `${m} `) + t);

  const submit = () => {
    if (!pick || !recipients) return;
    launch.mutate({
      name, channel: "sms", templateKey: null, smsBody: message.trim(),
      audience: pick.spec as Record<string, unknown>, audienceLabel: pick.label, recipients,
      scheduledFor: mode === "later" ? new Date(at).toISOString() : new Date().toISOString(),
    });
  };

  return (
    <SlideOver open={open} onClose={onClose} size="lg"
      title={<span className="inline-flex items-center gap-2"><MessageSquare className="h-4 w-4 text-brand-600" /> New text campaign</span>}
      subtitle="Marketing texts go only to customers with an opt-in on record."
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-400">
            {launch.isSuccess ? "" : n > 0 ? `Sends ${estimateDuration(n)} at 100 every 10 minutes, 9 AM – 8 PM Central.` : "Pick an audience to see who's opted in."}
          </span>
          <div className="flex items-center gap-2">
            {launch.isSuccess ? (
              <button type="button" onClick={onClose} className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800">Done</button>
            ) : (
              <>
                <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
                <button type="button" disabled={!ready} onClick={submit}
                  title={canSend ? undefined : "Sending campaigns needs the marketing.send permission — ask the owner under Settings → People & Access"}
                  className={cn("inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50", live ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700")}>
                  <Send className="h-4 w-4" /> {live ? (mode === "now" ? `Start texting ${n.toLocaleString()}` : `Schedule for ${n.toLocaleString()}`) : `Create held campaign for ${n.toLocaleString()}`}
                </button>
              </>
            )}
          </div>
        </div>
      }>
      <div className="space-y-5">
        {/* Audience */}
        <section>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Audience</label>
          <select value={pickKey} onChange={(e) => setPickKey(e.target.value)} className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-800 focus:border-brand-500 focus:outline-none">
            <option value="">Choose an audience…</option>
            {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          {pick && (
            <div className="mt-2 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-[12.5px] text-slate-700">
              {counting ? (
                <span className="text-slate-400">Counting who's opted in…</span>
              ) : count ? (
                <>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span><strong className={cn("text-base tabular-nums", count.sendable > 0 ? "text-brand-700" : "text-slate-500")}>{count.sendable.toLocaleString()}</strong> opted in to marketing texts</span>
                    {(count.not_opted_in ?? 0) > 0 && <span className="text-slate-500">{(count.not_opted_in ?? 0).toLocaleString()} have a cell but no opt-in on record</span>}
                    {count.no_endpoint > 0 && <span className="text-slate-400">{count.no_endpoint.toLocaleString()} no cell on file</span>}
                    <span className="text-slate-400">{count.matched.toLocaleString()} match the audience</span>
                  </div>
                  {count.sample.length > 0 && <div className="mt-0.5 text-[11px] text-slate-400">e.g. {count.sample.map((n) => displayCustomerName(null, n)).join(", ")}</div>}
                  {count.sendable === 0 && (
                    <div className="mt-1.5 flex items-start gap-1.5 text-[11.5px] text-amber-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Nobody in this audience has opted in to marketing texts yet. Texts need a yes on record — see how to collect one below. (Email doesn't: it uses unsubscribe.)</div>
                  )}
                </>
              ) : null}
            </div>
          )}
        </section>

        {/* Message */}
        <section>
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</label>
            <InfoTip label="About the opt-out line">“Reply STOP to opt out” is added to the end unless you write STOP yourself.</InfoTip>
            <span className="ml-auto flex items-center gap-1 text-[11px] text-slate-500">
              Insert:
              <button type="button" onClick={() => insertToken("{{first_name}}")} className="rounded-md border border-hairline bg-white px-1.5 py-0.5 font-medium text-slate-600 hover:bg-slate-50">First name</button>
              <button type="button" onClick={() => insertToken("{{portal_url}}")} className="rounded-md border border-hairline bg-white px-1.5 py-0.5 font-medium text-slate-600 hover:bg-slate-50">Portal link</button>
            </span>
          </div>
          <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi {{first_name}}, fall furnace tune-ups are open — members book first. Reply BOOK or call (952) 881-9000."
            className="w-full rounded-lg border border-hairline bg-white px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none" />
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
            <span className={cn("tabular-nums", seg.segments > 2 ? "text-amber-700" : "text-slate-500")}>{seg.chars} characters · {seg.segments} text{seg.segments === 1 ? "" : "s"}{seg.segments > 1 ? " (each segment is billed separately)" : ""}</span>
            {seg.unicode && <span className="text-amber-700">“{seg.culprit}” makes this a Unicode text — 70 characters per segment instead of 160. Plain quotes and dashes keep it short.</span>}
            {tooLong && <span className="text-rose-700">Keep a marketing text under 600 characters.</span>}
          </div>
          {message.trim() && (
            <div className="mt-2 flex justify-end">
              <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-brand-700 px-3 py-2 text-[13px] leading-snug text-white shadow-e1">{sample}</div>
            </div>
          )}
        </section>

        {/* Name + when */}
        <section className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Campaign name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Fall tune-up text — members" className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-800 focus:border-brand-500 focus:outline-none" />
          </label>
          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">When</span>
            <div className="flex items-center gap-1 rounded-full bg-white p-0.5 ring-1 ring-hairline">
              {(["now", "later"] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={cn("rounded-full px-3 py-1 text-xs font-semibold", mode === m ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50")}>{m === "now" ? "Start now" : "Pick a time"}</button>
              ))}
            </div>
          </div>
          {mode === "later" && (
            <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-800 focus:border-brand-500 focus:outline-none sm:col-span-2" />
          )}
          <p className="text-[11px] text-slate-500 sm:col-span-2">Texts go out between 9 AM and 8 PM Central. Anything scheduled outside that window starts at 9 AM.{!live ? " Comms are in testing: this campaign is created and held — nothing goes out until you press Release after go-live." : ""}</p>
        </section>

        {/* Opt-in help */}
        <section className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2.5 text-[12px] text-slate-600">
          <button type="button" onClick={() => setShowHelp((v) => !v)} className="inline-flex items-center gap-1.5 font-semibold text-slate-700 hover:text-slate-900"><Users className="h-3.5 w-3.5 text-brand-600" /> How customers opt in to marketing texts {showHelp ? "▾" : "▸"}</button>
          {showHelp && (
            <ol className="mt-1.5 list-decimal space-y-1 pl-5">
              <li><strong>They tell you.</strong> On the customer's page, open People &amp; Lines and click the Texts → Marketing pill on their cell to <em>Opted in</em>. It records who did it and when.</li>
              <li><strong>They text OFFERS.</strong> Anyone who texts OFFERS (or JOIN) to your Podium number is opted in on the spot and gets a confirmation text. Put “Text OFFERS to (952) 881-9000 for seasonal reminders” in an email or on an invoice.</li>
              <li><strong>Texting START</strong> after a STOP only lifts the block — it is not an opt-in.</li>
            </ol>
          )}
        </section>

        {launch.isError && <p className="text-xs text-rose-700">{(launch.error as Error).message}</p>}
        {launch.isSuccess && (
          <p className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {launch.data.status === "held" ? "Text campaign created and held" : "Text campaign scheduled"} — {launch.data.recipient_count.toLocaleString()} queued{launch.data.excluded_count ? `, ${launch.data.excluded_count} excluded` : ""}. It's in the Campaigns list.</p>
        )}
      </div>
    </SlideOver>
  );
}
